import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import api from "../../services/api";


// 📌 Fetch all categories
export const fetchCategories = createAsyncThunk(
  "categories/fetchAll",
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await api.get("/category/categories");
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: "Something went wrong" });
    }
  }
);


// 📌 Add new category (Admin)
export const addCategory = createAsyncThunk(
  "categories/add",
  async (formData, { rejectWithValue }) => {
    try {
      const { data } = await api.post("/category/create", formData, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" },
      });
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: "Something went wrong" });
    }
  }
);


// 📌 Update category (Admin)
export const updateCategory = createAsyncThunk(
  "categories/update",
  async ({ id, formData }, { rejectWithValue }) => {
    try {
      const { data } = await api.put(`/category/update/${id}`, formData, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" },
      });
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: "Something went wrong" });
    }
  }
);

// 📌 Delete category (Admin)
export const deleteCategory = createAsyncThunk(
  "categories/delete",
  async (id, { rejectWithValue }) => {
    try {
      const { data } = await api.delete(`/category/delete/${id}`, { withCredentials: true });
      return { ...data, id };
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: "Something went wrong" });
    }
  }
);


// Initial State
const initialState = {
  categories: [],
  loading: false,
  error: null,
  success: null,
};

const categorySlice = createSlice({
  name: "categories",
  initialState,
  reducers: {
    clearCategoryMessages: (state) => {
      state.error = null;
      state.success = null;
    },
  },
  extraReducers: (builder) => {
    // Fetch all
    builder
      .addCase(fetchCategories.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchCategories.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload.success) {
          state.categories = action.payload.data;
        } else {
          state.error = action.payload.message;
        }
      })
      .addCase(fetchCategories.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload?.message;
      })

      // Add
      .addCase(addCategory.fulfilled, (state, action) => {
        if (action.payload.success) {
          state.categories.unshift(action.payload.data);
          state.success = action.payload.message;
        } else {
          state.error = action.payload.message;
        }
      })
      .addCase(addCategory.rejected, (state, action) => {
        state.error = action.payload?.message;
      })

      // Update
      .addCase(updateCategory.fulfilled, (state, action) => {
        if (action.payload.success) {
          const index = state.categories.findIndex(
            (cat) => cat.categoryId === action.payload.data.categoryId
          );
          if (index !== -1) {
            state.categories[index] = action.payload.data;
          }
          state.success = action.payload.message;
        } else {
          state.error = action.payload.message;
        }
      })
      .addCase(updateCategory.rejected, (state, action) => {
        state.error = action.payload?.message;
      })

      // Delete
      .addCase(deleteCategory.fulfilled, (state, action) => {
        if (action.payload.success) {
          state.categories = state.categories.filter(
            (cat) => cat.categoryId !== action.payload.id
          );
          state.success = action.payload.message;
        } else {
          state.error = action.payload.message;
        }
      })
      .addCase(deleteCategory.rejected, (state, action) => {
        state.error = action.payload?.message;
      });
  },
});

export const { clearCategoryMessages } = categorySlice.actions;
export default categorySlice.reducer;
